/**
 * WCAG contrast math for tenant brand colors — Ted 10-09.
 *
 * Ted, on the dog profile's vet email and phone: the "blue blends with all
 * the other blue". They were painted `--brand-primary`, and HTHD's primary
 * (#62A2C3) is a light blue: about 2.8:1 on the profile card, well under the
 * 4.5:1 WCAG AA asks of body text. A light primary is normal for a brand —
 * it is chosen for buttons and logos, not for small text — so no tenant's
 * primary can be trusted as a text color.
 *
 * `accessibleLinkColor` derives one that can: the tenant's primary, darkened
 * toward black (hue kept, so the link still reads as the tenant's colour)
 * just until it clears AA on every surface it sits on. A primary that is
 * already dark enough (Switchback's pine) is returned unchanged. This is the
 * DESIGN-PHILOSOPHY Principle 6 rule — WCAG-safe derivations are COMPUTED,
 * never hand-picked — done in numbers instead of a fixed `color-mix` ratio
 * that is only right for the tenant it was tuned on.
 *
 * Pure; no DOM. `applyBrandKit` publishes the result as `--brand-link`.
 */

export type Rgb = { r: number; g: number; b: number };

/** WCAG AA for normal-size text. */
export const WCAG_AA_TEXT = 4.5;

/**
 * Aim a little above the line, so a browser's own rounding of a mixed colour
 * can never land the rendered value at 4.49.
 */
const TARGET_RATIO = 4.6;

/** `#rgb` / `#rrggbb` (case-insensitive) → channels 0–255; null otherwise. */
export function parseHex(hex: string | null | undefined): Rgb | null {
  if (typeof hex !== 'string') return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const c = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/**
 * CSS `color-mix(in srgb, a <weightA>, b)`: a per-channel weighted average in
 * gamma-encoded sRGB, which is exactly what the stylesheet's mixes compute
 * (the `--ferro-neutral-*` scale is `color-mix(in srgb, secondary N%, white)`).
 */
export function mixSrgb(a: Rgb, b: Rgb, weightA: number): Rgb {
  const w = Math.min(1, Math.max(0, weightA));
  return {
    r: Math.round(a.r * w + b.r * (1 - w)),
    g: Math.round(a.g * w + b.g * (1 - w)),
    b: Math.round(a.b * w + b.b * (1 - w)),
  };
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1–21. Order-independent. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

/**
 * The card surface the admin app paints content on: `--ferro-surface` =
 * `--ferro-neutral-50` = `color-mix(in srgb, var(--brand-secondary) 4%, white)`
 * (packages/ui/src/tokens.css). Kept in step with that file by
 * apps/admin-app/src/__tests__/brand-link-contrast.test.ts.
 */
export function brandSurface(colors: { secondary: string }): Rgb {
  const secondary = parseHex(colors.secondary) ?? { r: 0x2f, g: 0x3e, b: 0x46 };
  return mixSrgb(secondary, WHITE, 0.04);
}

/** Every background a link may sit on: white, the card surface, the page. */
export function linkBackgrounds(colors: {
  secondary: string;
  background?: string | null | undefined;
}): Rgb[] {
  const out = [WHITE, brandSurface(colors)];
  const page = parseHex(colors.background ?? null);
  if (page) out.push(page);
  return out;
}

/**
 * The tenant's primary, darkened toward black only as far as it must be to
 * reach `TARGET_RATIO` against EVERY background in `linkBackgrounds`. Null
 * when the primary is not a parseable hex — the stylesheet's fallback
 * (`--brand-primary-strong`) then applies, rather than a guess.
 */
export function accessibleLinkColor(colors: {
  primary: string;
  secondary: string;
  background?: string | null | undefined;
}): string | null {
  const primary = parseHex(colors.primary);
  if (!primary) return null;
  const backgrounds = linkBackgrounds(colors);
  const worst = (c: Rgb) => Math.min(...backgrounds.map((bg) => contrastRatio(c, bg)));
  // 1% steps: fine enough that the result is the lightest passing shade, and
  // at most 100 iterations. Black (step 100) clears 4.6 on any background
  // light enough to be a card, so the loop always returns.
  for (let step = 0; step <= 100; step += 1) {
    const candidate = mixSrgb(primary, BLACK, 1 - step / 100);
    if (worst(candidate) >= TARGET_RATIO) return toHex(candidate);
  }
  return toHex(BLACK);
}
